import { baseApi, resourceListTag, resourceScopeTag, resourceTag } from '@krp/client-runtime'

import { AQSI_TAG_TYPES, AQSI_TAGS } from '../../../shared/api/tags'
import type { AqsiRegisterCheck, AqsiTraceDeepState, AqsiTraceSale, AqsiTraceSales } from '../model/types'
import { decodeRegisterCheck, decodeTraceDeep, decodeTraceSale, decodeTraceSales } from './contracts'
import { AQSI_TRACE_URLS } from './urls'

/** Сколько последних продаж просит экран диагностики. */
export const TRACE_SALES_LIMIT = 30

export interface TraceSalesArgs {
  resourceId: string
}

/** Подробный режим держится не дольше этого, мин (по умолчанию договора — 30, выключается сам). */
export const TRACE_DEEP_MINUTES = 30
/** Как часто экран продажи с правом перечитывает флаг подробного режима, мс. */
export const TRACE_DEEP_POLL_MS = 60_000

export interface TraceDeepArgs {
  resourceId: string
}

export interface SetTraceDeepArgs {
  resourceId: string
  enabled: boolean
}

export interface TraceSaleArgs {
  resourceId: string
  saleId: string
}

/**
 * Диагностика продаж в ОБЩЕМ `baseApi`: чтения, переключатель подробного режима и замер кассы (команды — только по нажатию). rid — в аргументах и тегах, `resourceScopeTag(rid)` у каждого:
 * сброс одного кабинета не перечитывает другой.
 */
export const traceApi = baseApi.enhanceEndpoints({ addTagTypes: AQSI_TAG_TYPES }).injectEndpoints({
  endpoints: (build) => ({
    aqsiTraceSales: build.query<AqsiTraceSales, TraceSalesArgs>({
      query: ({ resourceId }) => ({
        url: AQSI_TRACE_URLS.sales(resourceId),
        params: { limit: TRACE_SALES_LIMIT },
        decode: decodeTraceSales,
      }),
      providesTags: (_result, _error, { resourceId }) => [
        resourceListTag(AQSI_TAGS.trace, resourceId),
        resourceScopeTag(resourceId),
      ],
    }),
    aqsiTraceSale: build.query<AqsiTraceSale, TraceSaleArgs>({
      query: ({ resourceId, saleId }) => ({
        url: AQSI_TRACE_URLS.sale(resourceId, saleId),
        decode: (raw: unknown) => decodeTraceSale(raw, saleId),
      }),
      providesTags: (_result, _error, { resourceId, saleId }) => [
        resourceTag(AQSI_TAGS.trace, resourceId, saleId),
        resourceScopeTag(resourceId),
      ],
    }),
    aqsiTraceDeep: build.query<AqsiTraceDeepState, TraceDeepArgs>({
      query: ({ resourceId }) => ({ url: AQSI_TRACE_URLS.deep(resourceId), decode: decodeTraceDeep }),
      providesTags: (_result, _error, { resourceId }) => [resourceTag(AQSI_TAGS.traceDeep, resourceId, 'state'), resourceScopeTag(resourceId)],
    }),
    /** Включить/выключить подробный режим. Идемпотентно по смыслу (состояние, а не приращение), повтор безопасен. */
    aqsiSetTraceDeep: build.mutation<AqsiTraceDeepState, SetTraceDeepArgs>({
      query: ({ resourceId, enabled }) => ({
        url: AQSI_TRACE_URLS.deep(resourceId),
        method: 'PUT',
        body: { enabled, minutes: enabled ? TRACE_DEEP_MINUTES : null },
        decode: decodeTraceDeep,
      }),
      invalidatesTags: (_result, _error, { resourceId }) => [resourceTag(AQSI_TAGS.traceDeep, resourceId, 'state')],
    }),
    /** Контрольный замер кассы: пять служебных чтений кассе (без печати и чека). Только по нажатию. */
    aqsiTraceRegisterCheck: build.mutation<AqsiRegisterCheck, TraceDeepArgs>({
      query: ({ resourceId }) => ({
        url: AQSI_TRACE_URLS.registerCheck(resourceId),
        method: 'POST',
        body: {},
        noRetry: true,
        timeoutMs: 60_000,
        decode: decodeRegisterCheck,
      }),
    }),
  }),
})

export const { aqsiTraceSales, aqsiTraceSale, aqsiTraceDeep, aqsiSetTraceDeep, aqsiTraceRegisterCheck } = traceApi.endpoints
